/**
 * CrewShowPanel — audience-facing view of the crew pipeline.
 *
 * Renders the trace as a left/right alternating chat: agent nameplate + speech
 * bubble in each agent's signature color. This is "the show the audience
 * would hear": Wire calls in the topic, Scribe validates, Mercury approves,
 * Quill writes the script, Frame sets the scene, Stenographer logs it.
 */
import React from 'react';
import { dialogueFromRun, CREW_VOICES } from './crewDialogue';

const NAMEPLATE = {
  fontSize: 10,
  letterSpacing: '0.22em',
  fontWeight: 600,
  fontFamily: 'ui-monospace, Menlo, monospace',
  opacity: 0.85,
};

function Bubble({ line, index }) {
  const onLeft = index % 2 === 0;
  const color = line.color || '#dbe3f2';
  if (line.segment_divider) {
    return (
      <div
        data-testid={`show-divider-${index}`}
        style={{
          margin: '18px auto 14px',
          maxWidth: 720,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          color: '#d4a574',
          fontSize: 10,
          letterSpacing: '0.32em',
          fontFamily: 'ui-monospace, Menlo, monospace',
        }}
      >
        <span style={{ flex: 1, height: 1, background: 'linear-gradient(90deg, transparent, rgba(212,165,116,0.45))' }} />
        <span>{line.label || line.line}</span>
        <span style={{ flex: 1, height: 1, background: 'linear-gradient(90deg, rgba(212,165,116,0.45), transparent)' }} />
      </div>
    );
  }
  return (
    <div
      data-testid={`show-bubble-${index}`}
      style={{
        display: 'flex',
        flexDirection: onLeft ? 'row' : 'row-reverse',
        gap: 10,
        marginBottom: 12,
      }}
    >
      <div
        style={{
          flexShrink: 0,
          width: 44,
          height: 44,
          borderRadius: 22,
          background: `${color}18`,
          border: `1.5px solid ${color}`,
          color,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 14,
          fontWeight: 600,
          fontFamily: 'Georgia, serif',
        }}
        aria-hidden="true"
      >
        {line.name?.slice(0, 1) || '·'}
      </div>
      <div style={{ maxWidth: '80%' }}>
        <div style={{ ...NAMEPLATE, color, marginBottom: 4 }}>
          {line.name?.toUpperCase() || '—'}
          {line.host && (
            <span style={{ marginLeft: 8, color: '#f8d28c' }}>· HOST</span>
          )}
          {line.role && (
            <span style={{ opacity: 0.5, marginLeft: 8 }}>· {line.role}</span>
          )}
          {line.message_type && (
            <span style={{ opacity: 0.45, marginLeft: 8 }}>· {String(line.message_type).toUpperCase()}</span>
          )}
        </div>
        <div
          style={{
            background: 'rgba(20,26,36,0.9)',
            border: `1px solid ${color}33`,
            borderLeft: onLeft ? `3px solid ${color}` : `1px solid ${color}33`,
            borderRight: onLeft ? `1px solid ${color}33` : `3px solid ${color}`,
            borderRadius: onLeft ? '2px 12px 12px 12px' : '12px 2px 12px 12px',
            padding: '10px 14px',
            color: '#e5ebf3',
            fontSize: 14,
            lineHeight: 1.55,
            fontFamily: 'Georgia, "Iowan Old Style", serif',
          }}
        >
          {line.line}
        </div>
        {(line.round || line.source_confidence) && (
          <div style={{ marginTop: 4, fontSize: 9, color: 'rgba(200,210,230,0.45)', fontFamily: 'ui-monospace, Menlo, monospace' }}>
            {line.round ? `ROUND ${line.round}` : ''}
            {line.source_confidence ? `${line.round ? ' · ' : ''}SOURCE CONF ${Number(line.source_confidence).toFixed(2)}` : ''}
          </div>
        )}
      </div>
    </div>
  );
}

function dialoguePacketBubbles(packet) {
  if (!packet || !Array.isArray(packet.messages)) return [];
  return packet.messages.map((msg) => {
    if (msg.segment_divider) {
      return { segment_divider: true, label: msg.label, line: msg.text, round: msg.round };
    }
    const voice = CREW_VOICES[msg.agent] || {
      name: msg.display_name || msg.agent,
      role: msg.role,
      color: msg.color || '#dbe3f2',
    };
    return {
      ...voice,
      name: msg.display_name || voice.name,
      role: msg.role || voice.role,
      color: msg.color || voice.color,
      line: msg.text,
      host: msg.host,
      round: msg.round,
      message_type: msg.message_type,
      source_confidence: msg.source_confidence,
    };
  });
}

export default function CrewShowPanel({ run, improvisedBubbles = null, running = false }) {
  const packet = run?.dialogue || run?.crew_packet?.live_dialogue;
  const engineBubbles = dialoguePacketBubbles(packet);
  const fallback = dialogueFromRun(run);
  // If LLM-improvised bubbles were provided, map agent → voice and merge
  // with the deterministic fallback so unknown agents still render.
  const bubbles = Array.isArray(improvisedBubbles) && improvisedBubbles.length
    ? improvisedBubbles.map((b) => {
        const voice = CREW_VOICES[b.agent] || { name: b.agent, color: '#dbe3f2' };
        return { ...voice, line: b.line };
      })
    : engineBubbles.length
      ? [
          ...engineBubbles,
          { segment_divider: true, label: 'STRUCTURED ANALYSIS BEGINS', line: 'STRUCTURED ANALYSIS BEGINS' },
          ...fallback,
        ]
      : fallback;
  if (!bubbles.length) {
    return (
      <div
        data-testid="show-empty"
        style={{
          color: 'rgba(200,210,230,0.45)',
          fontSize: 13,
          fontStyle: 'italic',
          padding: '10px 0',
          fontFamily: 'Georgia, serif',
        }}
      >
        No show yet — click RUN SHOW PREP to hear the crew talk through a topic.
      </div>
    );
  }

  // Find the Quill draft (final script) to render as a "reading room" block.
  const quillStage = (run?.trace || []).find((s) => s.agent === 'quill');
  const draftText = quillStage?.output?.text || '';
  const draftIsReal =
    draftText && !draftText.startsWith('[offline-stub]') && !draftText.startsWith('[llm-error]');

  return (
    <div data-testid="crew-show-panel" style={{ paddingBottom: 20 }}>
      {packet?.now_discussing && (
        <div
          data-testid="now-discussing-banner"
          style={{
            marginBottom: 14,
            padding: '14px 18px',
            borderRadius: 12,
            background: 'linear-gradient(135deg, rgba(212,165,116,0.18), rgba(77,226,192,0.08))',
            border: '1px solid rgba(212,165,116,0.35)',
            boxShadow: '0 18px 50px rgba(0,0,0,0.28)',
          }}
        >
          <div style={{ fontSize: 10, letterSpacing: '0.32em', color: '#d4a574', marginBottom: 6 }}>
            NOW DISCUSSING · {packet.host_agent?.toUpperCase() || 'PRODUCER'} HOSTING
          </div>
          <div style={{ fontFamily: 'Georgia, serif', fontSize: 22, color: '#f8ecd2', lineHeight: 1.25 }}>
            {packet.now_discussing}
          </div>
        </div>
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          marginBottom: 14,
          paddingBottom: 8,
          borderBottom: '1px solid rgba(120,150,200,0.2)',
        }}
      >
        <div style={{ fontSize: 11, letterSpacing: '0.28em', color: '#d4a574' }}>
          ON AIR · CREW DIALOGUE ENGINE
        </div>
        <div style={{ fontSize: 10, color: 'rgba(200,210,230,0.5)' }}>
          {Object.keys(CREW_VOICES).length} voices · {bubbles.length} handoffs
        </div>
      </div>

      {running && (
        <div data-testid="crew-thinking-indicator" style={{ marginBottom: 12, color: '#7dd3fc', fontSize: 11, letterSpacing: '0.18em' }}>
          PANEL THINKING · Producer is steering the next handoff...
        </div>
      )}

      <div style={{ maxWidth: 880, margin: '0 auto' }}>
        {bubbles.map((b, i) => (
          <Bubble key={i} line={b} index={i} />
        ))}
      </div>

      {/* Final script — read-aloud block */}
      {draftIsReal && (
        <div
          data-testid="show-script-block"
          style={{
            marginTop: 20,
            padding: '20px 24px',
            background: 'rgba(24,20,13,0.9)',
            border: '1px solid rgba(212,165,116,0.35)',
            borderRadius: 10,
          }}
        >
          <div
            style={{
              fontSize: 11,
              letterSpacing: '0.28em',
              color: '#d4a574',
              marginBottom: 10,
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <span>HOST READS — FINAL SCRIPT</span>
            <span style={{ opacity: 0.6 }}>
              {quillStage.output.word_count} words · {quillStage.output.segment}
            </span>
          </div>
          <div
            style={{
              fontFamily: 'Georgia, "Iowan Old Style", serif',
              color: '#f3e5c3',
              fontSize: 17,
              lineHeight: 1.8,
              whiteSpace: 'pre-wrap',
            }}
          >
            {draftText}
          </div>
        </div>
      )}
    </div>
  );
}
